import React, { useState } from 'react';
import { Camera, ScanText, Upload, Check, X, Loader2 } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { aiApi } from '../lib/api';
import { fileToBase64 } from '../lib/voice';

/**
 * Zulassungsschein Scanner: Photo/PDF upload -> OCR -> auto-fill vehicle fields.
 *
 * Props:
 *  - onExtracted(result, sourceImage) - called with extracted fields
 */
export default function ZulassungsscheinScanner({ onExtracted, testid = 'zulassungsschein-scanner' }) {
  const camRef = React.useRef(null);
  const fileRef = React.useRef(null);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [last, setLast] = useState(null);

  const handleFile = async (file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) {
      toast.error('Datei zu groß (max 8 MB)');
      return;
    }
    if (!/^image\/(jpeg|png|webp|heic|heif)$/.test(file.type) && file.type !== 'application/pdf') {
      toast.error('Bitte JPG/PNG/WEBP oder PDF hochladen');
      return;
    }
    if (file.type === 'application/pdf') {
      toast.error('PDF wird derzeit nicht unterstützt - bitte ein Foto vom Zulassungsschein machen');
      return;
    }
    setBusy(true);
    try {
      const { dataUrl, base64 } = await fileToBase64(file);
      setPreview(dataUrl);
      const { data } = await aiApi.ocrVehicle(base64);
      const res = data.result || {};
      if (!res || res.confidence === 0 || (!res.license_plate && !res.make && !res.vin)) {
        toast.error('Keine Fahrzeugdaten erkannt. Bitte deutlicheres Foto verwenden.');
      } else {
        toast.success('Fahrzeugdaten erkannt');
      }
      setLast(res);
      onExtracted?.(res, dataUrl);
    } catch (e) {
      console.error(e);
      toast.error('Erkennung fehlgeschlagen. Bitte manuell ausfüllen.');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid={testid} className="space-y-3">
      <Card className="rounded-2xl border-[hsl(var(--accent))]/30 bg-gradient-to-br from-blue-50/70 to-white">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <div className="h-10 w-10 rounded-xl bg-[hsl(var(--accent))] text-white flex items-center justify-center"><ScanText className="h-5 w-5" /></div>
            <div className="flex-1">
              <div className="font-heading font-semibold text-slate-900">Zulassungsschein scannen</div>
              <div className="text-xs text-slate-600">Foto machen — KI füllt Kennzeichen, Marke, Modell, FIN, Erstzulassung & Leistung automatisch aus.</div>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <Button type="button" onClick={() => camRef.current?.click()} disabled={busy} className="bg-[hsl(var(--accent))] hover:bg-[hsl(217_90%_48%)] text-white" data-testid="zs-scan-camera">
              {busy ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Camera className="h-4 w-4 mr-1.5" />}
              {busy ? 'Lese...' : 'Zulassungsschein fotografieren'}
            </Button>
            <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={busy} data-testid="zs-scan-file">
              <Upload className="h-4 w-4 mr-1.5" /> Datei wählen
            </Button>
            <input ref={camRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => e.target.files && handleFile(e.target.files[0])} data-testid="zs-camera-input" />
            <input ref={fileRef} type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files && handleFile(e.target.files[0])} data-testid="zs-file-input" />
          </div>
        </CardContent>
      </Card>

      {preview && (
        <div className="flex gap-3 items-start">
          <img src={preview} alt="Zulassungsschein Preview" className="w-28 h-28 object-cover rounded-xl border border-slate-200" />
          <div className="text-sm space-y-1">
            {last && last.confidence > 0 ? (
              <>
                <div className="font-medium text-slate-900 inline-flex items-center gap-1"><Check className="h-4 w-4 text-green-600" /> Erkannt mit {Math.round(last.confidence * 100)}% Konfidenz</div>
                <div className="text-slate-600">
                  {last.license_plate && <div>Kennzeichen: <b>{last.license_plate}</b></div>}
                  {(last.make || last.model) && <div>Fahrzeug: <b>{last.make} {last.model}</b></div>}
                  {last.vin && <div>FIN: <b>{last.vin}</b></div>}
                </div>
              </>
            ) : (
              <div className="text-slate-600 inline-flex items-center gap-1"><X className="h-4 w-4 text-red-600" /> Keine Daten erkannt</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
